import { $convertFromMarkdownString } from '@lexical/markdown';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import classNames from 'classnames';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { isMobile } from 'react-device-detect';
import { ErrorCode, useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import { MainPageHelmet } from 'src/analyticsComponents/MainPageHelmet';
import { TaskView } from 'src/components/FlatAppearance/components/TaskView';
import { ModalWithGallery } from 'src/components/ModalWithGallery';
import { OnboardingModal } from 'src/components/OnboardingModal';
import {
  LOCAL_STORAGE_VALUE,
  MAX_FILE_SIZE,
  REDIRECT_FROM,
  REFERRAL_ID,
  SHOWN_ONBOARDING_MODAL,
  SN_REFERRAL_CODE_COOKIE_NAME,
} from 'src/constants';
import AuthContext from 'src/contexts/AuthContext';
import SessionContext from 'src/contexts/SessionContext';
import ThreadInputBoxContext from 'src/contexts/ThreadInputBoxContext';
import {
  useAttachFile,
  useAvatarPreferences,
  useBreakpoint,
  useChatHistory,
  useChatPageQueryParams,
  useGetUser,
  useLeftPanelActions,
  useRightSidePanelState,
  useSession,
  useSpeechRecognizer,
} from 'src/hooks';
import { useUpgradeDowngradeParams } from 'src/hooks/useUpgradeDowngradeParamsHandler';
import { WelcomeModal } from 'src/pages/AuthPages/components/WelcomeModal';
import { DnDOverlay } from 'src/pages/ManageTasksChatPage/components/DnDOverlay';
import { modalsControlState, useAppSelector } from 'src/store';
import { editorConfig } from 'src/v2/components/Editor/constants';
import { RightSidePanel } from 'src/v2/components/RightSidePanel';
import styles from './ManageTasksChatPage.module.scss';
import { ConversationsHeader } from './components/ConversationsHeader';
import { MultiConversationsPanel } from './components/MultiConversationsPanel';
import { PromoTooltip } from './components/PromoTooltip/PromoTooltip';
// import { useNavigate } from 'react-router-dom';
import { TRANSFORMERS_LIST } from 'src/v2/components/Editor/constants/transformers';
import { ProductNotificationModal } from 'src/components/ProductNotificationModal';
import { FreeTierUpsellModalNewPlans } from 'src/components/FreeTierUpsellModalNewPlans';
import { MigrationToSNInfoModal } from 'src/pages/ManageTasksChatPage/components/MigrationToSNInfoModal';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { env } from 'src/env';
import Cookies from 'js-cookie';

export const ManageTasksChatPage = () => {
  const { isNinjaRetired } = useChatHistory();

  const { isMobileOrTablet } = useBreakpoint();
  const { toggleLeftPanelExpanded } = useLeftPanelActions();

  const isMounted = useRef<boolean>(false);

  useEffect(() => {
    if (isNinjaRetired && isMobileOrTablet && !isMounted.current) {
      toggleLeftPanelExpanded(true);
      isMounted.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNinjaRetired, isMobileOrTablet]);

  const { appUser: user, isOpenTier } = useSession();
  // const navigate = useNavigate();
  const { isFreeTier, isStarterTier } = useGetUser();
  const disableRequestsInMNForFreeTier = useFeatureFlagEnabled(
    'disableRequestsInMNForFreeTier',
  );

  const { isGuestAccess } = useContext(AuthContext);
  const { isRightPanelExpanded, isRightPanelFullWidth } =
    useRightSidePanelState();
  const { avatarAudioLocale } = useAvatarPreferences();
  useChatPageQueryParams();
  const { isVisibleModalGallery } = useContext(SessionContext);

  const { isVisibleWelcomeModal } = useAppSelector(modalsControlState);

  useUpgradeDowngradeParams();

  const {
    setThreadInputBoxValue,
    onToggleInputBoxExpanded,
    isInputBoxExpanded,
  } = useContext(ThreadInputBoxContext);

  const { handleUploadedFiles } = useAttachFile();

  const { getRootProps, isDragActive } = useDropzone({
    onDropAccepted: async (files) => {
      if (isOpenTier) {
        toast.error(
          'File attachment is not available on your current plan. Please upgrade ',
        );
        return;
      }
      const uploadedFiles = (files || []) as File[];
      await handleUploadedFiles(uploadedFiles);
    },
    onDropRejected: async (fileRejections) => {
      fileRejections.forEach((item) => {
        item.errors.forEach((error) => {
          switch (error.code) {
            case ErrorCode.FileTooLarge: {
              toast.error(
                `Sorry, the attached file "${item.file.name}" is too large to process. Please upload a smaller one and try again.`,
              );
              break;
            }
            default:
              break;
          }
        });
      });
    },
    noClick: true,
    disabled:
      isVisibleModalGallery ||
      isRightPanelExpanded ||
      ((isFreeTier || isStarterTier) && disableRequestsInMNForFreeTier),
    multiple: true,
    maxSize: MAX_FILE_SIZE,
  });

  useEffect(() => {
    if (
      !isVisibleWelcomeModal &&
      isOpenTier &&
      !isMobile &&
      localStorage.getItem(SHOWN_ONBOARDING_MODAL) !== LOCAL_STORAGE_VALUE
    ) {
      // todo set here open tooltip for app switcher
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenTier]);

  useEffect(() => {
    if (isOpenTier) {
      console.log('[ManageTasksChatPage] isOpenTier');
      return;
    }

    const redirectFrom = localStorage.getItem(REDIRECT_FROM);
    if (redirectFrom) {
      // navigate(redirectFrom);
      console.log('[REDIRECT_FROM] try to redirect to', redirectFrom);
      localStorage.removeItem(REDIRECT_FROM);
    }

    const referralId = localStorage.getItem(REFERRAL_ID);
    if (!!referralId) {
      console.log(
        '[REFERRAL_ID] referralId exists, redirect to SN',
        referralId,
      );
      localStorage.removeItem(REFERRAL_ID);

      const setCookie = () => {
        const cookieDomain = env.REACT_APP_COOKIE_DOMAIN;
        console.log('[ManageTasksChatPage] Cookie domain:', {
          cookieDomain,
          hasCookieDomain: !!cookieDomain,
        });

        if (cookieDomain) {
          console.log('[ManageTasksChatPage] Setting referral cookie', {
            cookieName: SN_REFERRAL_CODE_COOKIE_NAME,
            referralId,
            domain: cookieDomain,
            expires: 365,
          });

          Cookies.set(SN_REFERRAL_CODE_COOKIE_NAME, referralId, {
            domain: cookieDomain,
            expires: 365,
            secure: true,
            sameSite: 'Lax',
          });

          console.log('[ManageTasksChatPage] Cookie set successfully');
        }
      };

      // Set cookie twice to ensure persistence
      setCookie();
      window.addEventListener('beforeunload', setCookie, { once: true });

      window.location.href = env.REACT_APP_SUPER_NINJA_URL;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenTier]);

  const handleSpeechRecognizing = useCallback(
    (newValue: string) => {
      setThreadInputBoxValue(newValue);
      if (newValue && !isInputBoxExpanded) {
        onToggleInputBoxExpanded(true);
      }
    },
    [isInputBoxExpanded, setThreadInputBoxValue, onToggleInputBoxExpanded],
  );

  const { startSpeechRecognizing, stopSpeechRecognizing } = useSpeechRecognizer(
    {
      onRecognizing: handleSpeechRecognizing,
      sourceLanguage: avatarAudioLocale,
    },
  );

  const conversationsPanelProps = {
    startSpeechRecognizing,
    stopSpeechRecognizing,
  };

  const props = { ...getRootProps() };

  //  don't wait for user_id in the guest mode
  return user.user_id || isGuestAccess ? (
    <>
      <MainPageHelmet />

      <div
        className={styles.root}
        ref={props.ref}
        onDrop={props.onDrop}
        onDragLeave={props.onDragLeave}
        onDragEnter={props.onDragEnter}
      >
        {isDragActive && <DnDOverlay />}

        <LexicalComposer
          initialConfig={{
            ...editorConfig,
            editorState: () =>
              // TODO(olha): WIP Editor
              $convertFromMarkdownString('', TRANSFORMERS_LIST),
          }}
        >
          <div className={styles.container}>
            <div
              className={classNames(styles.middleContainer, {
                [styles.fadeAnimation]: isRightPanelFullWidth,
              })}
            >
              <ConversationsHeader />
              {/* TODO(olha): rename to ThreadMainContainer */}
              <div className={styles.threadContainerWrapper}>
                <MultiConversationsPanel {...conversationsPanelProps} />
              </div>
            </div>

            <RightSidePanel />
          </div>
        </LexicalComposer>

        <TaskView />

        <ModalWithGallery />

        <PromoTooltip />

        <OnboardingModal />

        <WelcomeModal />

        <FreeTierUpsellModalNewPlans />

        <ProductNotificationModal />

        <MigrationToSNInfoModal />
      </div>
    </>
  ) : (
    <></>
  );
};
